import { formatCurrency } from "@tellescope/utils";
import { CurrencyInput, Input, Label, MultiSelect, TabbedContainer, TabsContent } from "@tellescope/ui";
import { Button, Select } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { CUSTOM_PRICE_TAB, PRODUCTS_TAB, useChargePaymentForm } from "./useChargePaymentForm";

export interface ChargePaymentFormProps {
  contactId: string;
  onSubmit?: () => void;
  onCancel?: () => void;
}

export const ChargePaymentForm = ({ contactId, onSubmit, onCancel }: ChargePaymentFormProps) => {
  const { t } = useTranslation("payments");
  const { form, tabs, stripeAccounts, products, isCharging } = useChargePaymentForm({ contactId, onSubmit });

  return (
    <form className="flex flex-col gap-4" {...form.getFormProps()}>
      <div className="space-y-2">
        <div className="space-y-2 p-1 w-1/2">
          <Label htmlFor="stripeAccount">{t("Stripe Account")}</Label>
          <Select {...form.getCustomFieldProps("stripeAccountKey")}>
            <Select.Trigger id="stripeAccount" className="w-full">
              <Select.Value placeholder={t("Select Stripe Account")} />
            </Select.Trigger>
            <Select.Content>
              {stripeAccounts?.map(({ key, title }) => (
                <Select.Item key={key} value={key}>
                  {title}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </div>
        {!!form.values.stripeAccountKey && (
          <>
            <div className="border-t w-full" />
            <TabbedContainer
              tabs={tabs}
              showCard={false}
              value={form.values.type}
              onValueChange={(newVal) => form.setFieldValue("type", newVal)}
              tabsClassName="mt-2"
            >
              <div className="flex flex-col gap-4">
                <TabsContent value={PRODUCTS_TAB}>
                  <div className="space-y-2 p-1 max-w-full">
                    <Label htmlFor="products">{t("Select Products")}</Label>
                    <div className="flex gap-4 items-center max-w-full">
                      <MultiSelect
                        id="products"
                        options={
                          products?.map(({ title, id }) => ({
                            label: title,
                            value: id,
                          })) ?? []
                        }
                        {...form.getCustomFieldProps("productIds")}
                        placeholder={t("Select Products")}
                        className="grow flex-1"
                      />

                      <div className="flex flex-1 gap-1 grow-0">
                        <p className="font-bold text-foreground min-w-max">{t("Total Cost: ")}</p>
                        <p className="text-muted-foreground">{formatCurrency(form.values.cost, "USD")}</p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value={CUSTOM_PRICE_TAB}>
                  <div className="flex gap-2">
                    <div className="space-y-2 p-1 grow">
                      <Label htmlFor="customCost">{t("Cost")}</Label>
                      <CurrencyInput
                        id="customCost"
                        placeholder={t("Cost")}
                        {...form.getFieldProps("cost")}
                        onChange={(value) => form.setFieldValue("cost", value ?? 0)}
                      />
                    </div>
                    <div className="space-y-2 p-1 w-1/2">
                      <Label htmlFor="currency">{t("Currency")}</Label>
                      {/* TODO: support other currencies */}
                      <Select value="USD">
                        <Select.Trigger id="currency" className="w-full">
                          <Select.Value />
                        </Select.Trigger>
                        <Select.Content>
                          <Select.Item value="USD">{t("USD")}</Select.Item>
                        </Select.Content>
                      </Select>
                    </div>
                  </div>
                </TabsContent>
                <div className="space-y-2 p-1 max-w-full">
                  <Label htmlFor="purchaseDescription">{t("Purchase Description")}</Label>
                  <Input
                    id="purchaseDescription"
                    placeholder={t("Enter a Purchase Description")}
                    {...form.getFieldProps("description")}
                  />
                </div>
              </div>
            </TabbedContainer>
          </>
        )}
      </div>
      <div className="w-full flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button disabled={!form.isValid || isCharging} type="submit">
          {t("Charge Card on File")}
        </Button>
      </div>
    </form>
  );
};
